import { Bell, Command, CreditCard, Download, Eye, LogOut, Plus, Search, Share2, Sparkles, Wand2, RefreshCw, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { ModeSwitch } from "./ModeSwitch";
import { VoiceButton } from "./VoiceButton";
import { useNeural } from "@/store/neuralStore";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/useAuth";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export function NeuralTopBar() {
  const { user, isAdmin, signOut } = useAuth();
  const setPaletteOpen = useNeural((s) => s.setPaletteOpen);
  const autoUpdate = useNeural((s) => s.autoUpdate);
  const setAutoUpdate = useNeural((s) => s.setAutoUpdate);
  const refreshModels = useNeural((s) => s.refreshModels);
  const modelsLastUpdated = useNeural((s) => s.modelsLastUpdated);
  const [refreshing, setRefreshing] = useState(false);
  const [usage, setUsage] = useState({ tokens: 2_410_000, cost: 12.84 });

  useEffect(() => {
    const id = setInterval(() => {
      setUsage((u) => ({ tokens: u.tokens + Math.floor(Math.random() * 800), cost: +(u.cost + Math.random() * 0.02).toFixed(2) }));
    }, 2200);
    return () => clearInterval(id);
  }, []);

  // Auto-update simulado: a cada 7 dias
  useEffect(() => {
    if (!autoUpdate) return;
    const week = 7 * 24 * 3600 * 1000;
    if (Date.now() - modelsLastUpdated > week) {
      refreshModels();
      toast.success("Models registry auto-updated (weekly)");
    }
  }, [autoUpdate, modelsLastUpdated, refreshModels]);

  const updateNow = async () => {
    setRefreshing(true);
    await new Promise((r) => setTimeout(r, 700));
    refreshModels();
    setRefreshing(false);
    toast.success("Model registry refreshed — 22 models indexed");
  };

  const since = Math.floor((Date.now() - modelsLastUpdated) / 60000);

  return (
    <header className="h-14 border-b border-border glass flex items-center px-3 gap-3 z-20 relative">
      <ModeSwitch />

      {/* Command palette */}
      <button
        onClick={() => setPaletteOpen(true)}
        aria-label="Abrir command palette (Ctrl+K)"
        className="flex-1 max-w-md flex items-center gap-2 h-9 px-3 rounded-md bg-surface-2/60 hover:bg-surface-2 border border-border text-muted-foreground text-sm transition-colors"
      >
        <Search className="w-4 h-4" />
        <span className="flex-1 text-left">Command anything... agents, tools, modes</span>
        <kbd className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-surface-3 border border-border flex items-center gap-0.5">
          <Command className="w-2.5 h-2.5" /> K
        </kbd>
      </button>

      {/* Models update controls */}
      <div className="hidden lg:flex items-center gap-2 h-9 px-2.5 rounded-md glass border-primary/20">
        <button
          onClick={updateNow}
          className="flex items-center gap-1.5 text-[11px] font-mono text-primary hover:text-primary-glow"
          title={`Last update ${since}m ago`}
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Update Models
        </button>
        <div className="w-px h-4 bg-border" />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-muted-foreground">Auto/wk</span>
          <Switch checked={autoUpdate} onCheckedChange={setAutoUpdate} className="scale-75" />
        </div>
      </div>

      {/* Usage live */}
      <div className="hidden xl:flex items-center gap-3 px-3 h-9 rounded-md glass border-primary/20">
        <div className="flex flex-col">
          <span className="text-[9px] font-mono uppercase text-muted-foreground tracking-wider">Tokens</span>
          <span className="text-xs font-mono text-foreground">{(usage.tokens / 1e6).toFixed(2)}M</span>
        </div>
        <div className="w-px h-6 bg-border" />
        <div className="flex flex-col">
          <span className="text-[9px] font-mono uppercase text-muted-foreground tracking-wider">Cost</span>
          <span className="text-xs font-mono text-success">${usage.cost.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex items-center gap-1">
        <VoiceButton />
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title="AR/VR/BCI Preview"
          onClick={() => toast("👁 AR/VR/BCI preview — coming in v22")}
        >
          <Eye className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 relative"
          title="Notifications"
          onClick={() => toast("🔔 No new notifications")}
        >
          <Bell className="w-4 h-4" />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-accent shadow-glow-magenta" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title="Share P2HM Link"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(window.location.href);
              toast.success("🔗 Link copied to clipboard");
            } catch {
              toast.error("Could not copy link");
            }
          }}
        >
          <Share2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title="Export Universe"
          onClick={() => {
            const state = useNeural.getState();
            const blob = new Blob(
              [JSON.stringify({ agents: state.agents, schedules: state.schedules, memory: state.memory, sandboxRuns: state.sandboxRuns }, null, 2)],
              { type: "application/json" },
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `p2hm-universe-${Date.now()}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("📦 Universe exported");
          }}
        >
          <Download className="w-4 h-4" />
        </Button>
        <div className="w-px h-6 bg-border mx-1" />
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 border-warning/40 text-warning hover:bg-warning/10 hover:text-warning"
          title="Buy more credits"
          aria-label="Buy Credits"
          data-testid="buy-credits-btn"
          onClick={() => window.open("https://lovable.dev/settings/billing", "_blank", "noopener,noreferrer")}
        >
          <CreditCard className="w-3.5 h-3.5" /> Buy Credits
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 border-secondary/30 text-secondary hover:bg-secondary/10 hover:text-secondary"
          onClick={() => toast.success("🩹 Self-Heal scan started — 0 issues found")}
        >
          <Wand2 className="w-3.5 h-3.5" /> Self-Heal
        </Button>
        <Button
          size="sm"
          className="h-9 gap-1.5 bg-gradient-neural text-primary-foreground hover:opacity-90 shadow-glow-cyan border-0"
          onClick={() => {
            useNeural.getState().setMode("classic");
            window.dispatchEvent(new CustomEvent("neural-nav", { detail: "builder" }));
            toast.success("✨ New Universe — opening Builder Studio");
          }}
        >
          <Plus className="w-3.5 h-3.5" /> New Universe
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-2 w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent grid place-items-center text-xs font-bold text-background hover:opacity-90 transition">
              {(user?.email ?? "?").charAt(0).toUpperCase()}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs">
              <div className="font-mono truncate">{user?.email}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">{isAdmin ? "Admin" : "User"}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => window.open("https://lovable.dev/settings/billing", "_blank", "noopener,noreferrer")}
              data-testid="buy-credits-menu"
            >
              <CreditCard className="w-4 h-4 mr-2 text-warning" /> Billing → Add Credits
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut().then(() => (window.location.href = "/auth"))} className="text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
